import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { PD } from "@/constants/testIds";
import { loadSearchResults, sanitizeSearchContent } from "@/lib/pageDesignerSearch";

/**
 * Runtime Search content. The card renders a search box and the rows the
 * server returned. The browser sends the saved source id, the saved field ids
 * and the text typed — nothing else — and receives only the values of those
 * fields.
 */
export default function PageDesignerSearchContent({ card, preview = false }) {
  const config = sanitizeSearchContent(card?.searchContent);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);
  const [status, setStatus] = useState("idle");

  const configKey = `${config.sourceId}|${config.fields.join(",")}|${config.maxResults}`;

  useEffect(() => {
    let cancelled = false;
    const term = query.trim();
    if (term) setStatus("loading");
    loadSearchResults(config, term).then((result) => {
      if (cancelled) return;
      setItems(result.items || []);
      setFields(result.fields && result.fields.length ? result.fields : []);
      setStatus(result.status);
    });
    return () => {
      cancelled = true;
    };
    // Intentional: re-run when the saved configuration or the typed text changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey, query]);

  const title = config.title || "Search";
  const columns = fields.length ? fields : config.fields.map((id) => ({ id, label: id }));

  let body = null;
  if (!config.sourceId) {
    body = <p className="epd__table-empty">Configure this search.</p>;
  } else if (!config.fields.length) {
    body = <p className="epd__table-empty">Select the fields this search box should search.</p>;
  } else if (status === "empty-query" || status === "idle") {
    body = (
      <p className="epd__table-empty" data-testid={`${PD.searchStatus}-${card.id}`}>
        Type to search.
      </p>
    );
  } else if (status === "loading") {
    body = (
      <p className="epd__table-empty" data-testid={`${PD.searchStatus}-${card.id}`}>
        Searching…
      </p>
    );
  } else if (status === "error") {
    body = (
      <p className="epd__table-empty" data-testid={`${PD.searchStatus}-${card.id}`}>
        Unable to run this search.
      </p>
    );
  } else if (!items.length) {
    body = (
      <p className="epd__table-empty" data-testid={`${PD.searchStatus}-${card.id}`}>
        No matches.
      </p>
    );
  } else {
    body = (
      <div className="epd__table-wrap">
        <table className="epd__data-table" data-testid={`${PD.searchResults}-${card.id}`}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.id}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr key={item.id || `${card.id}-result-${index}`}>
                {columns.map((column) => (
                  <td key={column.id}>{(item.values || {})[column.id] || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div
      className="epd__search"
      data-testid={`${PD.searchContent}-${card.id}`}
      data-preview={preview ? "true" : "false"}
      data-source-id={config.sourceId}
    >
      <div className="epd__table-title" data-testid={`${PD.searchHeading}-${card.id}`}>
        {title}
      </div>
      <div className="epd__search-box">
        <Search className="epd__search-icon" aria-hidden="true" />
        <input
          className="epd__input"
          type="search"
          data-testid={`${PD.searchInput}-${card.id}`}
          aria-label={title}
          placeholder="Search…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onPointerDown={(event) => event.stopPropagation()}
        />
      </div>
      {body}
    </div>
  );
}
